/**
 * The agent portal: its destinations, its rail, and the words on the
 * representations list.
 *
 * An agent — an independent advisor, or a member reading a firm's workspace —
 * comes here to see which works other people have put in their hands. This
 * file is the portal spec the shell mounts and the copy that list draws from;
 * how the mandates are grouped and counted is `agent.model.ts`, and the reads
 * are `useRepresentationsController.ts`. The other screens on the rail each
 * own their own copy.
 *
 * TWO OF THE DESIGN'S CONTROLS HAVE ARRIVED AND ONE HAS NOT. The expiry tile
 * and the days-remaining line were absent because "a mandate has no term", and
 * `20261003096000_mandate_terms_and_lifecycle.sql` gives it one — an optional
 * `expires_at`, enforced by `ondo.live_mandate_ids()` in the statement that
 * asks. So both are built, and the comments that refused them are rewritten
 * below rather than left to contradict the schema.
 *
 * WHAT IS STILL ABSENT, and still for a reason about the registry rather than
 * about the design: the STATUS filter's Expired chip, because
 * `ondo.my_agent_mandates()` reads the liveness set and a lapsed mandate is
 * therefore never in this list at all — an agent whose term ran out is a former
 * agent, and 20260901090000's refusal to hand one the photograph of a work
 * stands. Expiry belongs on the appointing side's screen, where renewing it is
 * an act that exists. And the colour filter, which needs a fingerprint nothing
 * computes.
 */

import { definePortal } from "../shell/portal.ts";

/**
 * Where inside the agent portal the reader is.
 *
 * The union this file used to have was implicit and empty: the agent workspace
 * was ONE screen, and `resolveWorkspaceRoute` said so in capitals — "THE AGENT
 * WORKSPACE HAS EXACTLY ONE DESTINATION, and it is `work`". That was true while
 * an agent was a person with a list of mandates. It stopped being true when a
 * FIRM could hold a mandate: Marlow & Finch has members, an inbox, and a trail,
 * and none of those fit on a list.
 */
export type AgentDestination =
  | "representations"
  | "share-codes"
  | "request-representation"
  | "verifications"
  | "notifications"
  | "notification-preferences"
  | "about"
  | "activity"
  | "profile"
  | "work";

/**
 * The agent workspace's own copy.
 *
 * The empty and error strings belong to the representations LIST, which is now
 * one screen in a portal rather than the whole of it. Its own `title` is gone —
 * the portal header carries the heading, and `AGENT_PORTAL.headers` below is
 * where "My Representations" is written.
 */
export const AGENT_COPY = {
  loading: "Loading your mandates…",
  /**
   * Says how a mandate begins, because an agent cannot start one.
   *
   * Only a work's owner may appoint an agent, so an empty screen with no
   * explanation would read as a broken workspace to the one person who has no
   * control to press.
   */
  empty: "No work has been assigned to you yet.",
  emptyDetail:
    "A collector names their agent from a work's record. When one does, the work appears here and you can open its full record.",
  errorTitle: "Can’t load your mandates",
  /** Singular and plural, for the count beside the heading. */
  count: (n: number) => `${n} ${n === 1 ? "mandate" : "mandates"}`,
  /**
   * "Assigned 14 March 2026", and now "· 121 days remaining" after it when
   * there is a term — which is what the design asked for all along.
   *
   * Was "Assigned by ONDO-1001" — a restatement of the client's id, which the
   * group head two lines above now carries. The note beside it used to say
   * "when the mandate began is recorded; when it ends is not", and that is no
   * longer true: `expiresAt` is on the row and `mandateDaysRemaining` derives
   * the number from it, never storing one. See `describeAssignment`.
   */
  assignedOn: "Assigned",
  /**
   * The term, said three ways, and the SHAPE of the sentence is the decision.
   *
   * "121 days remaining" is what the design asks for and what 20260901090000
   * refused, on the grounds that it "would put a deadline on screen that
   * nothing enforces". It is enforced now — the same column the permission sets
   * read — so the number is a fact rather than an invention, which is the whole
   * of what changed.
   *
   * `openEnded` IS DELIBERATELY EMPTY RATHER THAN "No end date". Open-ended is
   * the default and the commonest state, and labelling it would put a phrase on
   * nearly every row to say that nothing is going to happen. The absence of a
   * term reads as the absence of a term.
   *
   * `endsToday` exists because "0 days remaining" reads as expired and is not.
   */
  term: {
    openEnded: "",
    remaining: (days: number) => `${days} ${days === 1 ? "day" : "days"} remaining`,
    endsToday: "Ends today",
  },
  open: "View",
  eyebrow: "Agent Portal",
  navLabel: "Agent workspace",

  /** The card over the list: which account is reading, on what terms. */
  signedInAs: "Logged in as",

  /**
   * The five counts, and ENDING SOON is the one that used to be impossible.
   *
   * It was refused in these words: "EXPIRED / 'Needs renewal' is not here and
   * cannot be. A mandate has no term... A tile counting expiries would be
   * counting a lifecycle this registry does not have." The lifecycle exists now,
   * so the tile does.
   *
   * IT COUNTS WHAT IS ENDING, NOT WHAT HAS ENDED, and that distinction is
   * forced by the registry rather than chosen for the design. A lapsed mandate
   * is not in this list — `ondo.my_agent_mandates()` reads
   * `ondo.live_mandate_ids()`, so an agent whose term ran out stops seeing the
   * work entirely. An EXPIRED count here would therefore always be zero, which
   * is the dead chip the filter comment below still refuses. What an advisory
   * can act on is the term that is about to run out, which is also the only one
   * either side can still renew.
   *
   * ARTISTS keeps its place, because it is a real question an advisory asks of
   * its own book and the registry can answer it from the rows already on screen.
   */
  tiles: {
    works: "Active works",
    worksNote: "Currently authorised",
    clients: "Clients",
    clientsNote: "Who appointed you",
    artists: "Artists",
    artistsNote: "Across your mandates",
    pending: "Pending",
    pendingNote: "Requests awaiting an answer",
    endingSoon: "Ending soon",
    endingSoonNote: "Within 30 days",
  },

  /**
   * The filter over the list, and it is by ARTIST.
   *
   * The design's two chip rows are STATUS — Active / Pending / Expired — and
   * CODE COLOUR, and neither can be built honestly. Every row here is active by
   * construction, because `ondo.my_agent_mandates()` returns open mandates only
   * and deliberately: returning an ended one would hand a former agent the
   * title, year and photograph of a work whose access was withdrawn. Pending
   * belongs to requests, which have a screen of their own — Request
   * Representation lists them, and a second copy here would be a second place to
   * read the same thing. And EXPIRED is still a dead chip, though no longer for
   * the reason it was: terms exist now, but a lapsed mandate leaves this list
   * the moment it lapses, for the disclosure reason two sentences above — so an
   * Expired chip here would filter to nothing, for ever. Expiry belongs on the
   * appointing side's screen, where renewing it is an act that exists. So a
   * status row would still be one live chip and two dead ones.
   *
   * CODE COLOUR filters the colour fingerprint, and `work.constants.ts` already
   * settled that: "The barcode is meant to be derived from the work's own
   * photographs… a decorative stand-in would look like something the registry
   * had measured." It is absent here for the same reason it is absent there.
   *
   * Artist is what is left, and it is not a consolation: an advisory holding
   * thirty works wants the six Doigs. Rendered only when there are two or more
   * artists to choose between — `CollectionToolbar`'s rule, that a control with
   * one option is chrome pretending to be a control.
   */
  artistFilter: "Artist",
  allArtists: "All",
  noMatches: "No work by that artist is assigned to you.",

  /**
   * On every row, because it is true of every row.
   *
   * A tag that never varies would normally be noise. This one is the single
   * claim the screen exists to make — you are authorised on this work today —
   * and the reason it never varies is stated above: an ended mandate is not
   * returned at all. It is the design's ACTIVE badge, and it means what the
   * design means by it.
   */
  active: "Active",

  /** "3 works" under a client's name. */
  worksUnder: (n: number) => `${n} ${n === 1 ? "work" : "works"}`,
  collapse: "Collapse",
  expand: "Expand",

  /** Shown in the issue form's place, to a seat below editor. */
  shareCodesNeedEditor:
    "Issuing a code widens who may read a work the firm represents, so it needs a seat at editor level or above. The codes the firm has issued are listed here for everyone.",
} as const;

/**
 * The agent portal.
 *
 * NOTHING on its rail is `planned` any more. Two items were, and each left when
 * the relation it named landed — which is what a `planned` entry is for: a
 * rail that silently omits half the product tells the reader nothing, while a
 * page naming what has to exist first tells them what ONDO is and is not, and
 * comes down the day it exists.
 *
 * "Share Codes / Initiate a Sale" was the last, and it is now "Share Codes /
 * Agree a Consignment" — OND-82, because the old second half named an act an
 * agent cannot perform. Its `notYet` said "a share code needs a grant shape of
 * its own that says who may re-share and for how long, and that shape has not
 * been argued yet." 20260910123000 is the argument — a redemption is a viewer
 * grant, so nobody re-shares; the code expires, and the issuer ending it
 * revokes what it gave. The second half of the label, agreeing a consignment,
 * is a different door and the screen says so.
 *
 * "Request Representation" WAS the second one, and its `notYet` said exactly
 * what had to exist first: "a request table, and… resolving an ONDO code you
 * were given into a record… bounded the way `principal_eligibility()` is: a
 * second fact you would only know if you had really been shown the work, and a
 * rate limit charged on the refusal." 20260831090000 is all four of those, so
 * the entry is gone rather than amended — a page naming what is missing is only
 * honest while something is.
 *
 * Every subtitle names the workspace's subject, which the collector's do not.
 * That is not decoration: a member of an advisory is reading screens about the
 * FIRM's mandates and the firm's inbox, and a subtitle saying "you" would be
 * quietly wrong about whose they are. On a personal workspace the same sentence
 * resolves to the reader, which is what makes it true for an independent
 * advisor with no firm at all.
 */
export const AGENT_PORTAL = definePortal<AgentDestination>({
  eyebrow: AGENT_COPY.eyebrow,
  navLabel: AGENT_COPY.navLabel,

  destinations: [
    { id: "representations", label: "My Representations", glyph: "◫", state: "ready", group: "main" },
    { id: "share-codes", label: "Share Codes / Agree a Consignment", glyph: "→", state: "ready", group: "main" },
    { id: "request-representation", label: "Request Representation", glyph: "↗", state: "ready", group: "main" },
    { id: "verifications", label: "Verifications", glyph: "✓", state: "ready", group: "main" },
    { id: "notifications", label: "Notifications", glyph: "⊘", state: "ready", group: "foot" },
    { id: "notification-preferences", label: "Notification Settings", glyph: "⚙", state: "ready", group: "foot" },
    { id: "about", label: "About ONDO", glyph: "i", state: "ready", group: "foot" },
    { id: "activity", label: "Activity & access log", glyph: "≡", state: "ready", group: "foot" },
    // NOT IN THE DESIGN, and listed anyway — the one departure on this rail.
    // The identity card at the foot opens it, so the screen is reachable either
    // way; what a nav item adds is that a member can find where their own record
    // and the firm's team are without discovering that a card happens to be
    // clickable. The collector's rail makes the same argument in the same words.
    { id: "profile", label: "My Profile", glyph: "◉", state: "ready", group: "foot" },
    { id: "work", label: "Artwork record", glyph: "◈", state: "unlisted", group: "main" },
  ],

  headers: {
    representations: {
      title: "My Representations",
      // The id as well as the name. This is the workspace's front page, and the
      // firm's ONDO id is what a client is given to name it in a mandate — so
      // the one screen that is entirely about mandates is where it belongs.
      subtitle: (subject) => `Artworks assigned to ${subject.name} (${subject.ondoId})`,
    },
    "share-codes": {
      // The full label, because `toNav` reads the header title for the rail —
      // `destinations[].label` is not what the rail renders — and the design
      // names both halves. The screen says which half it is.
      //
      // "AGREE A CONSIGNMENT" AND NOT "INITIATE A SALE", per OND-82: the second
      // half of this label named an act an agent cannot perform. A gallery
      // sells; an agent agrees the consignment that puts the work in the
      // gallery's hands, and only if their client granted them that. The design
      // names both halves and this is the second half said correctly.
      title: "Share Codes / Agree a Consignment",
      subtitle: (subject) =>
        `${subject.name} — show a work you represent to one person, for a while`,
    },
    "request-representation": {
      title: "Request Representation",
      subtitle: "Enter an ONDO code to request access to represent a work",
    },
    verifications: {
      title: "Verifications",
      subtitle: (subject) =>
        `${subject.name} — confirm or dispute ONDO information others have asked you to verify`,
    },
    // The FIRM's inbox on a firm workspace, and the subtitle says so for the
    // same reason Verifications' does: a member is reading what Marlow & Finch
    // was told, not what they were told, and "your notifications" would be
    // quietly wrong about whose they are.
    notifications: {
      title: "Notifications",
      subtitle: (subject) => `${subject.name} — what has been sent here, and what needs an answer`,
    },
    "notification-preferences": {
      title: "Notification Settings",
      subtitle:
        "How each kind of notification reaches you — immediately, in the daily digest, or not at all",
    },
    about: { title: "About ONDO", subtitle: "" },
    activity: {
      title: "Activity & access log",
      // The FEED half is still the caller's own trail, and that limit is
      // unchanged: `GET /v1/activity` takes a collection, a work or `scope=me`
      // and refuses to resolve a supplied id, so there is no org-scoped feed to
      // read. OND-68 did not fix that and did not need to.
      //
      // WHAT CHANGED IS THE SECOND HALF OF THE SENTENCE. The access trail IS
      // the firm's, read from `ondo.access_events` rather than from a feed —
      // append-only, undeletable by anybody including the service role, and
      // written inside the request that serves the read. The clause is hedged
      // because `access_events_read` gives an ordinary seat only their OWN
      // rows: promising every colleague's reads to a viewer would be the same
      // over-claim this subtitle has always refused, one rung down.
      subtitle:
        "Every act you have recorded, and who has opened a record — the firm's whole trail if you administer it, your own if not",
    },
    profile: {
      title: "My Profile",
      // Replaced at render for the firm's workspace, where the subject is the
      // organisation rather than the reader — see PROFILE_HEADERS and `profileAt`.
      subtitle: "Account, identity verification and membership",
    },
    work: {
      title: "Artwork record",
      subtitle: "What the registry holds for this work, and its chain of title",
    },
  },

  notYet: {},

  defaultDestination: "representations",

  records: {
    representations: "none",
    "share-codes": "none",
    "request-representation": "none",
    verifications: "none",
    notifications: "none",
    "notification-preferences": "none",
    about: "none",
    activity: "none",
    profile: "none",
    work: "required",
  },

  // Where a work record with no work goes. The collector's is their collection;
  // an agent HOLDS no collection — `agent` has no `from` clause and reaches the
  // work and stops — so the list of what they represent is the only place a
  // record can honestly be chosen from.
  recordFallback: "representations",

  // The inbox, scoped the way this portal's verification inbox already is.
  //
  // `agent.assigned` and `agent.removed` are addressed to whoever holds the
  // mandate, which on a firm workspace is the FIRM — so a member of Marlow &
  // Finch reading their own feed would find nothing, and this rail carried no
  // item at all rather than show them an empty one. `?principal=` on
  // `GET /v1/notifications` is what changed; `inboxPrincipalFor` picks the id,
  // the same function and the same argument as Verifications above.
  badges: { verifications: "verifications", notifications: "notifications", settlements: null },

  profileAt: "profile",
  // No greeting: this portal's front page is a list of other people's works, and
  // "Welcome back, Daniel" over a client's holdings is the wrong register.
  greetsAt: null,

  // No header action. Every act an agent may perform belongs to a work — writing
  // its history, answering a request about it — and there is nothing a portal
  // header could start. `registerWork` is false for an agent in any case: they
  // curate no collection to register into.
  primaryAction: () => null,
  secondaryAction: () => null,
});
